import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { toast, Toaster } from "sonner";
import { Loader2, LogIn, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success("Contraseña verificada. Envíamos un código a tu correo.");
      if (data?.email_error && !data?.email_delivered) {
        toast.warning(
          "Resend rechazó la entrega (free-tier). Usa el hint de TESTING_MODE si estás probando."
        );
      }
      navigate(
        `/verificar-correo?email=${encodeURIComponent(email)}&purpose=login`,
        { replace: false }
      );
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
              <LogIn size={12} /> Paso 1 de 2 · Contraseña
            </div>
            <h1 className="mt-3 font-display text-3xl font-semibold text-[#0F2942] tracking-tight">
              Iniciar sesión
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Después de tu contraseña te enviaremos un código de 6 dígitos por correo.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="rounded-xl border border-border bg-white p-6 sm:p-7 space-y-4"
            data-testid="login-form"
          >
            <GoogleSignInButton testid="login-google-btn" />

            <div className="flex items-center gap-3">
              <div className="h-px bg-border flex-1" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                O usa tu correo
              </span>
              <div className="h-px bg-border flex-1" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.mx"
                required
                autoComplete="email"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Tu contraseña"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}
                  data-testid="login-password-toggle"
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2563EB] hover:bg-[#1e4fd0] text-white h-11"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Verificando…
                </>
              ) : (
                "Continuar"
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center pt-1">
              ¿No tienes cuenta?{" "}
              <Link to="/registro" className="text-[#2563EB] font-medium hover:underline">
                Regístrate
              </Link>
            </p>
          </form>
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}
